/**
 * The icon set. Story VC-016.
 *
 * Inline SVG rather than an icon font or a sprite sheet, for three reasons
 * that all point the same way: an icon inherits `currentColor` and therefore
 * the M3 role of whatever contains it, there is no second request that can
 * fail after the page has rendered, and a `<title>` makes each one
 * addressable by a screen reader without a parallel `aria-label` to keep in
 * sync.
 *
 * The geometry is on a 24-unit grid with a 2-unit stroke, which is the
 * Material Symbols proportion — so these sit correctly beside anything a
 * deployment adds from that set later.
 *
 * Every path is drawn here rather than imported. A dependency for fourteen
 * shapes would be a supply chain for fourteen shapes.
 */

/**
 * @param {{ name: string, size?: number, title?: string,
 *           className?: string }} props
 */
export function Icon({ name, size = 24, title, className }) {
  const path = PATHS[name];
  if (!path) {
    return null;
  }
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      // Decorative unless it is given a name. An icon inside a labelled
      // button is decorative — announcing it would read the button twice.
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : "true"}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      {path}
    </svg>
  );
}

/**
 * The mark. Not in `PATHS` because it is filled rather than stroked and
 * carries the brand's two roles rather than `currentColor` alone.
 *
 * A waveform inside a rounded square: the shape a clinic's caller sees on a
 * home screen, and the one thing on this page that is allowed to be a logo.
 *
 * @param {{ size?: number, title?: string }} props
 */
export function Mark({ size = 40, title = "Clinic voice agent" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role="img"
      aria-label={title}
    >
      <rect
        x="2"
        y="2"
        width="44"
        height="44"
        rx="14"
        fill="currentColor"
        opacity="0.14"
      />
      <g
        stroke="currentColor"
        strokeWidth="3.4"
        strokeLinecap="round"
        fill="none"
      >
        <path d="M13 24v0" opacity="0.55" />
        <path d="M19 18v12" />
        <path d="M24.5 13v22" />
        <path d="M30 17v14" />
        <path d="M35.5 21v6" opacity="0.55" />
      </g>
    </svg>
  );
}

/** The shapes, by name. */
const PATHS = {
  call: (
    <path d="M6.5 3.5h3l1.8 4.4-2.2 1.6a12.5 12.5 0 0 0 5.4 5.4l1.6-2.2 4.4 1.8v3a2 2 0 0 1-2.2 2A16.8 16.8 0 0 1 4.5 5.7a2 2 0 0 1 2-2.2Z" />
  ),
  chat: (
    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4h13A1.5 1.5 0 0 1 20 5.5v9a1.5 1.5 0 0 1-1.5 1.5H9l-5 4Z" />
  ),
  hangup: (
    <>
      <path d="M3.2 10.3a14.5 14.5 0 0 1 17.6 0" />
      <path d="M6.6 12.6 4.9 15a1.4 1.4 0 0 1-2-.2l-1.3-1.6" />
      <path d="M17.4 12.6 19.1 15a1.4 1.4 0 0 0 2-.2l1.3-1.6" />
      <path d="M9.4 13.6h5.2v2.9H9.4z" />
    </>
  ),
  mic: (
    <>
      <rect x="9" y="2.5" width="6" height="11" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <path d="M12 18v3.5" />
    </>
  ),
  micOff: (
    <>
      <path d="M15 4.5a3 3 0 0 0-6 0v5" />
      <path d="M9 12.2a3 3 0 0 0 5 1.9" />
      <path d="M5 11a7 7 0 0 0 10.6 6" />
      <path d="M19 11a7 7 0 0 1-.4 2.3" />
      <path d="M12 18v3.5" />
      <path d="M3.5 3.5 20.5 20.5" />
    </>
  ),
  send: <path d="M4 11.5 20 4l-7.5 16-2-6.5Z" />,
  close: (
    <>
      <path d="M6 6 18 18" />
      <path d="M18 6 6 18" />
    </>
  ),
  back: (
    <>
      <path d="M19 12H5" />
      <path d="m11 6-6 6 6 6" />
    </>
  ),
  pulse: (
    <path d="M2 12h4l2.5-7 4 14 3-8.5L18.5 12H22" />
  ),
  history: (
    <>
      <path d="M3.5 12a8.5 8.5 0 1 0 2.6-6.1" />
      <path d="M3.2 4.5v4h4" />
      <path d="M12 8v4.4l3 1.8" />
    </>
  ),
  stop: <rect x="6" y="6" width="12" height="12" rx="2.5" />,
  speaker: (
    <>
      <path d="M4 9.5h3.2L12 5.5v13L7.2 14.5H4Z" />
      <path d="M16 9.2a4 4 0 0 1 0 5.6" />
      <path d="M18.6 6.6a7.5 7.5 0 0 1 0 10.8" />
    </>
  ),
  trash: (
    <>
      <path d="M4 6.5h16" />
      <path d="M9 6.5V4.8A1.3 1.3 0 0 1 10.3 3.5h3.4A1.3 1.3 0 0 1 15 4.8v1.7" />
      <path d="M6.5 6.5 7.3 19a1.5 1.5 0 0 0 1.5 1.4h6.4a1.5 1.5 0 0 0 1.5-1.4l.8-12.5" />
    </>
  ),
  warning: (
    <>
      <path d="M12 3.8 21.5 20H2.5Z" />
      <path d="M12 10v4.2" />
      <path d="M12 17.2v.1" />
    </>
  ),
  // The debug sheet's families and controls (RO-021, §30.13.6).
  inference: (
    <>
      <path d="M9.5 18h5" />
      <path d="M10.5 21h3" />
      <path d="M12 3a6 6 0 0 0-3.6 10.8c.7.5 1.1 1.3 1.1 2.2h5c0-.9.4-1.7 1.1-2.2A6 6 0 0 0 12 3Z" />
    </>
  ),
  tool: (
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4l-5.6 5.6a1.5 1.5 0 0 0 2.1 2.1l5.6-5.6a4 4 0 0 0 5.4-5.4l-2.4 2.4-2.2-.4-.4-2.2Z" />
  ),
  branch: (
    <>
      <circle cx="6" cy="5" r="2" />
      <circle cx="6" cy="19" r="2" />
      <circle cx="18" cy="8" r="2" />
      <path d="M6 7v10" />
      <path d="M6 14c0-3.5 3-6 7-6h3" />
    </>
  ),
  mail: (
    <>
      <rect x="3.5" y="5.5" width="17" height="13" rx="2.5" />
      <path d="m4.5 7.5 7.5 5.5 7.5-5.5" />
    </>
  ),
  flag: (
    <>
      <path d="M5.5 21V4" />
      <path d="M5.5 4.5h11l-2 4 2 4h-11" />
    </>
  ),
  agent: (
    <>
      <rect x="4.5" y="8" width="15" height="11" rx="3.5" />
      <path d="M12 4.5V8" />
      <path d="M9.5 13v.5" />
      <path d="M14.5 13v.5" />
    </>
  ),
  layers: (
    <>
      <path d="m12 4 8 4-8 4-8-4Z" />
      <path d="m4 12 8 4 8-4" />
      <path d="m4 16 8 4 8-4" />
    </>
  ),
  chevronDown: <path d="m6 9.5 6 6 6-6" />,
  chevronRight: <path d="m9.5 6 6 6-6 6" />,
  expand: (
    <>
      <path d="M4 9V4h5" />
      <path d="M20 9V4h-5" />
      <path d="M4 15v5h5" />
      <path d="M20 15v5h-5" />
    </>
  ),
  shrink: (
    <>
      <path d="M9 4v5H4" />
      <path d="M15 4v5h5" />
      <path d="M9 20v-5H4" />
      <path d="M15 20v-5h5" />
    </>
  ),
};
